import React from 'react';
import { useFormContext } from "react-hook-form";
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { 
  CreditCardIcon, 
  CarIcon, 
  Bath,
  RotateCwIcon,
  UtensilsIcon,
  XIcon
} from 'lucide-react';
import { usePaymentMethods } from '@/features/alojamientos/hooks/usePaymentMethods';
import { useParkingTypes } from '@/features/alojamientos/hooks/useParkingTypes';
import { usePoolTypes } from '@/features/alojamientos/hooks/usePoolTypes';
import { useSportTypes } from '@/features/alojamientos/hooks/useSportTypes';
import { useMenuTypes } from '@/features/alojamientos/hooks/useMenuTypes';
import { Skeleton } from '@/components/ui/skeleton';

interface MultiSelectOptionProps {
  value: string;
  label: string;
  onRemove: () => void;
}

const MultiSelectOption = ({ value, label, onRemove }: MultiSelectOptionProps) => (
  <Badge 
    key={value}
    variant="secondary"
    className="flex items-center gap-1"
  >
    {label}
    <button
      onClick={onRemove}
      className="ml-1 hover:bg-gray-200 rounded-full p-0.5"
    >
      <XIcon size={14} />
    </button>
  </Badge>
);

interface MultiSelectFieldProps {
  label: string;
  icon: React.ReactNode;
  fieldName: string;
  options: { value: string; label: string }[];
  readOnly?: boolean;
}

const MultiSelectField = ({ label, icon, fieldName, options, readOnly = false }: MultiSelectFieldProps) => {
  const { setValue, watch } = useFormContext();
  const selectedValues = watch(`services.${fieldName}`, []);

  const handleMultiSelect = (value: string) => {
    if (readOnly) return;
    
    const newValues = selectedValues.includes(value)
      ? selectedValues.filter((v: string) => v !== value)
      : [...selectedValues, value];
    setValue(`services.${fieldName}`, newValues);
  };

  return (
    <div className="form-group">
      <Label className="form-label flex items-center gap-2 mb-2">
        {icon}
        {label}
      </Label>
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2">
          {selectedValues.map((value: string) => {
            const option = options.find(opt => opt.value === value);
            return (
              <MultiSelectOption 
                key={value}
                value={value}
                label={option?.label || value}
                onRemove={() => handleMultiSelect(value)}
              />
            );
          })}
        </div>
        <Select
          onValueChange={handleMultiSelect}
          disabled={readOnly}
        >
          <SelectTrigger>
            <SelectValue placeholder="Seleccionar opciones" />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

interface MultiSelectGroupProps {
  readOnly?: boolean;
}

const MultiSelectGroup = ({ readOnly = false }: MultiSelectGroupProps) => {
  const { paymentMethods, isLoading: isLoadingPayments, error: paymentError } = usePaymentMethods();
  const { parkingTypes, isLoading: isLoadingParking, error: parkingError } = useParkingTypes();
  const { poolTypes, isLoading: isLoadingPool, error: poolError } = usePoolTypes();
  const { sportTypes, isLoading: isLoadingSports, error: sportsError } = useSportTypes();
  const { menuTypes, isLoading: isLoadingMenus, error: menuError } = useMenuTypes();

  const cardOptions = paymentMethods.map(method => ({
    value: method.id.toString(),
    label: method.name
  }));

  const parkingOptions = parkingTypes.map(type => ({
    value: type.id.toString(),
    label: type.name
  }));

  const poolOptions = poolTypes.map(type => ({
    value: type.id.toString(),
    label: type.name
  }));

  const recreationalSportsOptions = sportTypes.map(type => ({
    value: type.id.toString(),
    label: type.name
  }));

  const specialMenusOptions = menuTypes.map(type => ({
    value: type.id.toString(),
    label: type.name
  }));

  return (
    <div className="space-y-6 p-6 border rounded-lg bg-card">
      <h3 className="text-lg font-medium">Servicios Múltiples</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {isLoadingPayments ? (
          <div className="col-span-full">
            <Skeleton className="h-10 w-full" />
          </div>
        ) : paymentError ? (
          <div className="col-span-full text-red-500">
            {paymentError}
          </div>
        ) : (
          <MultiSelectField 
            label="Tarjetas" 
            icon={<CreditCardIcon size={16} />} 
            fieldName="cards" 
            options={cardOptions}
            readOnly={readOnly}
          />
        )}
        
        {isLoadingParking ? (
          <div className="col-span-full">
            <Skeleton className="h-10 w-full" />
          </div>
        ) : parkingError ? (
          <div className="col-span-full text-red-500">
            {parkingError}
          </div>
        ) : (
          <MultiSelectField 
            label="Cochera" 
            icon={<CarIcon size={16} />} 
            fieldName="parking" 
            options={parkingOptions}
            readOnly={readOnly}
          />
        )}
        
        {isLoadingPool ? (
          <div className="col-span-full">
            <Skeleton className="h-10 w-full" />
          </div>
        ) : poolError ? (
          <div className="col-span-full text-red-500">
            {poolError}
          </div>
        ) : (
          <MultiSelectField 
            label="Piscina" 
            icon={<Bath size={16} />} 
            fieldName="pool" 
            options={poolOptions}
            readOnly={readOnly}
          />
        )}
        
        {isLoadingSports ? (
          <div className="col-span-full">
            <Skeleton className="h-10 w-full" />
          </div>
        ) : sportsError ? (
          <div className="col-span-full text-red-500">
            {sportsError}
          </div>
        ) : (
          <MultiSelectField 
            label="Recreativo/Deportivo" 
            icon={<RotateCwIcon size={16} />} 
            fieldName="recreationalSports" 
            options={recreationalSportsOptions}
            readOnly={readOnly}
          />
        )}
        
        {isLoadingMenus ? (
          <div className="col-span-full">
            <Skeleton className="h-10 w-full" />
          </div>
        ) : menuError ? (
          <div className="col-span-full text-red-500">
            {menuError}
          </div>
        ) : (
          <MultiSelectField 
            label="Menús especiales" 
            icon={<UtensilsIcon size={16} />} 
            fieldName="specialMenus" 
            options={specialMenusOptions}
            readOnly={readOnly}
          />
        )}
      </div>
    </div>
  );
};

export default MultiSelectGroup;
